/**
 * GoogleBooksClient - Calls the backend proxy for Google Books API requests.
 * The proxy (server.js) handles external API calls to avoid CORS issues.
 */

const BACKEND_PROXY = (
    import.meta.env.VITE_PROXY_URL || 'http://localhost:4005'
).replace(/\/+$/, '');

/**
 * Search Google Books for books by query string.
 * @param {string} query - Search term
 * @returns {Promise<Array>} Array of book search results
 */
export async function searchBooks(query) {
    const res = await fetch(`${BACKEND_PROXY}/api/books/search?query=${encodeURIComponent(query)}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Book search failed: ${res.status}`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : data.items || [];
}

/**
 * Fetch full details for a book by Google Books volume ID.
 * @param {string} googleBooksId - Google Books volume ID
 * @returns {Promise<Object>} Full book details
 */
export async function getBookDetails(googleBooksId) {
    const res = await fetch(`${BACKEND_PROXY}/api/books/details/${encodeURIComponent(googleBooksId)}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Book details failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Download a book cover at a specific zoom level.
 * @param {string} googleBooksId - Google Books volume ID
 * @param {number} zoom - Zoom level (1=small, 2=medium, 3=large)
 * @returns {Promise<{ coverDataURL: string }>}
 */
export async function getBookCover(googleBooksId, zoom = 3) {
    const res = await fetch(`${BACKEND_PROXY}/api/books/cover/${encodeURIComponent(googleBooksId)}?zoom=${zoom}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Cover download failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Download a book cover from Open Library by ISBN.
 * @param {string} isbn - ISBN-10 or ISBN-13
 * @param {string} size - 'S', 'M', or 'L'
 * @returns {Promise<{ coverDataURL: string }>}
 */
export async function getBookCoverOpenLibrary(isbn, size = 'L') {
    const res = await fetch(`${BACKEND_PROXY}/api/books/cover/openlibrary/${encodeURIComponent(isbn)}?size=${size}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Open Library cover download failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Download a book cover from Open Library by numeric cover ID.
 * Useful when ISBN lookup fails — search results surface cover_i IDs for all editions.
 * @param {string} coverId - Open Library numeric cover ID
 * @param {string} size - 'S', 'M', or 'L'
 * @returns {Promise<{ coverDataURL: string }>}
 */
export async function getBookCoverByOLId(coverId, size = 'L') {
    const res = await fetch(`${BACKEND_PROXY}/api/books/cover/ol-id/${encodeURIComponent(coverId)}?size=${size}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Cover download failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Search Open Library for book covers across all editions.
 * Returns up to 15 unique cover IDs with preview URLs.
 * @param {string} title - Book title
 * @param {string} [author] - Author name (optional but improves accuracy)
 * @returns {Promise<{ covers: Array<{ coverId: string, previewUrl: string }> }>}
 */
export async function searchBookCoversOL(title, author) {
    const params = new URLSearchParams({ title });
    if (author) params.set('author', author);
    const res = await fetch(`${BACKEND_PROXY}/api/books/covers/search?${params}`);
    if (!res.ok) throw new Error('Open Library cover search failed');
    return res.json();
}
